/* eslint-disable @typescript-eslint/no-unused-expressions */
import { Check } from "lucide-react";
import { Task } from "./Plan";
import { PlanBuilderContext } from "./Plan";
import { useContext, useEffect, useState } from "react";
import { format } from "date-fns"
import { dateformat } from "@/utils/GlobalConstants";
import TrafficLightComponent from "../TrafficLightComponent";
import { AddIcon, CharterIcon, ChevronRightIcon, ChevronUpIcon, DeliverableIcon, DependenciesIcon, MilestoneIcon, PackageMilestoneIcon, StatusReportIcon, TaskIcon } from "../ui/Icons/TooltipIcon";
import { useSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { isSortable } from "@dnd-kit/react/sortable";
import { arrayMove } from "@dnd-kit/sortable";
import { AppContext } from "@/App";
import GanttObj from "./plan_gantt/GanttObj";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import DatePicker from "../ui/DatePicker";
import PackageMilestone from "../ui/Icons/PackageMilestone";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import FrmDependencies from "./frmDependencies";
import { PageLockContext } from "../PageLockProvider";

interface TaskProps {
  task: Task
  selected?: boolean
  primary?: boolean
  showGantt?: boolean
  index: number
  subTaskDepth: number
  groupId: string
  expanded?: boolean
}

const TaskObj = ({ task, selected, primary, showGantt: showGantt, index, subTaskDepth, groupId, expanded = true }: TaskProps) => {
  const tooltipDelay = 1000;
  const app = useContext(AppContext);
  const props = useContext(PlanBuilderContext);
  const lock = useContext(PageLockContext);

  const [movingTask, setMovingTask] = useState<Task | null>(null);
  const [localSubTasks, setLocalSubTasks] = useState<Task[]>(task.subTasks || []);
  const [renderVersion, setRenderVersion] = useState(0);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(expanded);

  const { ref, isDragging } = useSortable({
    id: "t" + task.id,
    index: index,
    type: 'task',
    accept: 'task',
    group: groupId,
    disabled: (props.multiSelect || app.authLevel < 4 || lock.viewOnly),
    modifiers: [RestrictToVerticalAxis]
  });
  const style = {
    // Outputs `translate3d(x, y, 0)`
    //transform: CSS.Translate.toString(transform),
  };

  const [earliestStartDate, setEarliestStartDate] = useState<Date>();
  const [newDuration, setNewDuration] = useState(task.duration);
  const [taskName, setTaskName] = useState(task.name);
  const [closedForDragging, setClosedForDragging] = useState(false);

  useEffect(() => {
    task.level = subTaskDepth;
  }, []);

  useEffect(() => {
    const dependencyObjs = getDependencyOptions();
    setEarliestStartDate(getEarliestStartDate(task, dependencyObjs));
  }, [task.dependencies]);

  useEffect(() => {
    setNewDuration(task.duration);
  }, [task.duration]);

  useEffect(() => {
    setTaskName(task.name);
  }, [task.name]);

  useEffect(() => {
    if (task.subTasks && task.subTasks.length > 0) {
      setOpen(!isDragging); // close the subtask list when dragging
    }
  }, [isDragging]);

  useEffect(() => {
    UpdateTaskStatus();
  }, [props.workstreams]);

  useEffect(() => {
    // Respond to expanded prop changes (for drag collapse/expand)
    setOpen(expanded);
  }, [expanded]);

  useEffect(() => {
    // Sync local state with task subtasks - use a fresh reference to ensure React detects changes
    if (task.subTasks) {
      // Sort by the saved index property to ensure correct order
      const sorted = [...task.subTasks].sort((a, b) => a.index - b.index);
      setLocalSubTasks(sorted);
      // Ensure list is open when subtasks are added
      if (task.subTasks.length > 0) {
        setOpen(expanded);
      }
    } else {
      setLocalSubTasks([]);
    }
  }, [task.subTasks, expanded]);

  useEffect(() => {
    if (props.tasksClosedForDragging.includes(task.id)) {
      setOpen(false);
      setClosedForDragging(true);
    } else if (closedForDragging) {
      setOpen(true);
      setClosedForDragging(false);
    }
  }, [props.tasksClosedForDragging, task.id, closedForDragging]);

  // useEffect(() => {
  //   updateDatesFromSubTasks();
  // }, [task.subTasks?.map(st => `${st.startDate}-${st.finishDate}`).join(',')]);

  let backgroundColor = "";

  if (selected) {
    if (task.isMilestone) {
      backgroundColor = "bg-planbuilder-milestone";
    } else {
      backgroundColor = "bg-planbuilder-task";
    }
  } else {
    if (task.isMilestone) {
      backgroundColor = "bg-planbuilder-milestone-unselected";
    } else {
      backgroundColor = "bg-planbuilder-task-unselected";
    }
  }

  function UpdateTaskStatus() {
    if (task.completed) {
      task.status = 4;
      return;
    }
    // check if task has dependents
    const workPackage = props.getWorkPackageById(task.workPackageId);

    if (!workPackage) {
      return;
    }

    if (new Date(workPackage!.finishDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      task.status = 1; // Past work package finish date = red
      return;
    }

    if (new Date(task.finishDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      // task is past due date and not completed
      if (task.hasDependents) {
        task.status = 1; // Past due with dependents = red
      } else {
        task.status = 2; // Past due without dependents = orange
      }
    }
  }

  function getDependencyOptions() {
    return task.dependencies.map((dependency) => {
      return props.getAllTasks()
        .find(t => t.id === dependency);
    });
  }

  function getAssignedUsername() {
    const users = app.teamMembers.concat(app.projectManagers);
    if (task.ownerId === "") {
      return "Unassigned";
    }
    const user = users.find(user => user.id === task.ownerId);
    if (user) {
      return user.displayName;
    }
    return "Unknown User";
  }

  function getStatusIcon() {
    if (task.status === 1 || task.status === 2) {
      return <div className="w-6 m-2 "><TrafficLightComponent TrafficLightColour={task.status} Size={6} /></div>;
    }

    return <div className="min-w-10"></div>;
  }

  function getCompletedIcon() {
    const styleStr = task.completed ? "bg-green-600" : "bg-gray-600 text-gray-500";

    return <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); updateTaskComplete(); }}>
      <Check className={"ml-2 " + styleStr + " rounded-md"} />
    </button>;
  }

  function getDisplayOnCharterIcon() {
    const handleClick = (e: React.MouseEvent) => {
      if (lock.viewOnly) {
        return;
      }
      e.stopPropagation();
      task.displayOnCharter = !task.displayOnCharter;
      props.updateTask(task.id, task);
    };

    const styleStr = task.displayOnCharter ? "" : "text-gray-500";
    return <button onClick={handleClick}>
      <CharterIcon delayDuration={tooltipDelay} className={"ml-2 " + styleStr} />
    </button>;
  }

  function getDisplayOnStatusReportIcon() {
    const handleClick = (e: React.MouseEvent) => {
      if (lock.viewOnly) {
        return;
      }
      e.stopPropagation();
      task.displayOnStatusReport = !task.displayOnStatusReport;
      props.updateTask(task.id, task);
    };

    const styleStr = task.displayOnStatusReport ? "" : "text-gray-500";
    return <button onClick={handleClick}>
      <StatusReportIcon delayDuration={tooltipDelay} className={"ml-2 " + styleStr} />
    </button>;
  }

  function getTaskIcon() {
    if (selected && app.authLevel >= 4 && !props.multiSelect && !lock.viewOnly) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="bg-transparent outline-none focus:outline-none">
              {task.isDeliverable && task.isMilestone ? <PackageMilestone className="m-2" /> : task.isDeliverable ? <DeliverableIcon delayDuration={tooltipDelay} className="m-2" /> : task.isMilestone ? <MilestoneIcon delayDuration={tooltipDelay} className="m-2" /> : <TaskIcon delayDuration={tooltipDelay} className="m-2" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-min" align="center">
            <DropdownMenuItem className="w-min" onClick={(e) => { e.stopPropagation(); task.isDeliverable = false; task.isMilestone = false; props.updateTask(task.id, task); }}>
              {<div className="flex flex-row"><TaskIcon className="m-2" /><span className="my-auto ml-2">Task</span></div>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); task.isDeliverable = true; task.isMilestone = false; props.updateTask(task.id, task); }}>
              {<div className="flex flex-row"><DeliverableIcon className="m-2" /><span className="my-auto ml-2">Deliverable</span></div>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); task.isMilestone = true; task.isDeliverable = false; props.updateTask(task.id, task); }}>
              {<div className="flex flex-row"><MilestoneIcon className="m-2" /><span className="my-auto ml-2">Milestone</span></div>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); task.isMilestone = true; task.isDeliverable = true; props.updateTask(task.id, task); }}>
              {<div className="flex flex-row"><PackageMilestoneIcon className="m-2" /><span className="my-auto ml-2 text-nowrap">Deliverable Milestone</span></div>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>)
    }

    if (task.isMilestone) {
      if (task.isDeliverable) {
        return <PackageMilestoneIcon delayDuration={tooltipDelay} className="m-2" />;
      }
      return <MilestoneIcon delayDuration={tooltipDelay} className="m-2" />;
    } else if (task.isDeliverable) {
      return <DeliverableIcon delayDuration={tooltipDelay} className="m-2" />;
    } else {
      return <TaskIcon delayDuration={tooltipDelay} className="m-2" />;
    }
  }

  function getTaskNameField() {
    if (selected && app.authLevel >= 4 && !props.multiSelect && !lock.viewOnly) {
      return <input id={task.id + "_TaskName"} type="text" className="grow bg-transparent focus:outline-none" style={{ width: (365 - subTaskDepth * 15) + "px" }} maxLength={50}
        value={taskName}
        onChange={(e) => {
          setTaskName(e.target.value);
        }}
        onFocus={(e => {
          e.target.select();

        })}
        onBlur={(e) => {
          task.name = e.target.value;
          props.updateTask(task.id, task);
        }}
        onClick={(e => {
          e.stopPropagation();
        })}
      />;
    }
    return <p className="truncate" style={{ width: (365 - subTaskDepth * 15) + "px" }}>{task.name}</p>;
  }

  function getTaskDurationField() {
    if (selected && app.authLevel >= 4 && !props.multiSelect
      && !task.completed && !lock.viewOnly &&
      (task.subTasks === undefined || task.subTasks === null || task.subTasks.length === 0)) {
      return <div className="flex flex-row ml-6">
        <input id={task.id + "_TaskDuration"} type="text" className="w-[30px] mr-1 bg-transparent focus:outline-none"
          style={{ textAlign: "right" }}
          pattern="[0-9]*"
          value={newDuration}
          onFocus={(e => {
            e.target.select();
          })}
          onChange={(e) => {
            const duration = parseInt(e.target.value);
            if (isNaN(duration) || duration < 1) {
              app.showActionNotification("Action Disallowed", "Duration must be a whole number, minimum 1 day", "default");
              return;
            }
            setNewDuration(duration);
          }}

          onBlur={(e) => {
            const duration = parseInt(e.target.value);
            if (isNaN(duration) || duration < 1) {
              app.showActionNotification("Action Disallowed", "Duration must be a whole number, minimum 1 day", "default");
              return;
            }
            task.finishDate = props.addWorkingDays(task.startDate, duration);
            props.updateTask(task.id, task);
          }}
          onClick={(e => {
            e.stopPropagation();
          })} /><span>{(newDuration > 1 ? " Days" : " Day")}</span></ div>;
    }
    return <div className="ml-4 w-[75px] text-right"><span>{task.duration + (task.duration > 1 ? " Days" : " Day ")}</span></div>;
  }

  function getTaskOwnerField() {
    const users = app.teamMembers.concat(app.projectManagers);

    if (selected && app.authLevel >= 4 && !lock.viewOnly) {
      return <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="ml-4 w-[100px] bg-transparent outline-none text-left focus:outline-none">
            {getAssignedUsername()}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {users.map((user) => (
            <DropdownMenuItem key={user.id}
              onClick={(e) => {
                e.stopPropagation();
                task.ownerId = user.id as string;
                props.updateTask(task.id, task);
                getAssignedUsername();
              }}
              className="flex flex-row justify-between">
              <div className="flex flex-row max-w-[200px]">{user.displayName}</div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem key={"unassigned"}
            onClick={(e) => {
              e.stopPropagation();
              task.ownerId = "";
              props.updateTask(task.id, task);
              getAssignedUsername();
            }}
            className="flex flex-row justify-between">
            <div className="flex flex-row max-w-[100px]">Unassigned</div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    }

    return <div className="ml-4 w-[200px] text-left">{getAssignedUsername()}</div>;
  }

  function setStartDate(date: Date) {
    if (props.isWorkingDay(date) === false) {
      app.showActionNotification("Action Disallowed", "Start date must be a working day", "default");
      return;
    }

    if (earliestStartDate && date.setHours(0, 0, 0, 0) < new Date(earliestStartDate).setHours(0, 0, 0, 0)) {
      app.showActionNotification("Action Disallowed", "Start date cannot be before " + format(earliestStartDate, dateformat), "default");
      return;
    }

    task.startDate = date;
    task.finishDate = props.addWorkingDays(task.startDate, task.duration);
    props.updateTask(task.id, task);
  }

  function getEarliestStartDate(task: Task, dependencyObjects: (Task | undefined)[]): Date {
    let startDate = new Date(props.projectStartDate);

    if (task.dependencies.length === 0) {
      return startDate;
    }
    else {
      // Update startDate based on the latest finish date of the dependencies
      let latestFinishDate = startDate ?? new Date();
      dependencyObjects.forEach((dependency) => {
        if (dependency && new Date(dependency.finishDate).setHours(0, 0, 0, 0) > new Date(latestFinishDate).setHours(0, 0, 0, 0)) {
          latestFinishDate = dependency.finishDate;
        }
      });

      startDate = latestFinishDate;
    }
    return startDate;
  }

  function getDateRangeField() {
    if (selected && app.authLevel >= 4 && !props.multiSelect && !task.completed && !lock.viewOnly) {
      return <div className="flex flex-row mx-auto">
        <DatePicker key={task.id + "_TaskStartDate"} ghost={true}
          date={task.startDate}
          setDate={(value: Date | ((prevState: Date) => Date)) => {
            setStartDate(value instanceof Function ? value(task.startDate) : value);
          }}
        />
        <div className="mx-1">-</div>
        {(task.subTasks === undefined || task.subTasks === null || task.subTasks.length === 0) ?
          <DatePicker key={task.id + "_TaskEndDate"} ghost={true}
            date={task.finishDate}
            setDate={(value: Date | ((prevState: Date) => Date)) => {
              // Handle both direct Date values and functional updates
              const date = value instanceof Function ? value(task.finishDate) : value;
              if (isNaN(date.getTime())) {
                app.showActionNotification("Error", "Invalid finish date", "destructive");
                return;
              }
              task.finishDate = date;
              task.duration = props.getWorkingDays(task.startDate, task.finishDate);
              props.updateTask(task.id, task);
            }}
          /> : format(task.finishDate, dateformat)}
      </div>;
    }
    return <span className="text-nowrap mx-auto">{format(task.startDate, dateformat)} - {format(task.finishDate, dateformat)}</span>;
  }

  function disableComplete(): boolean {
    const result = props.canCompleteTask(task.id, lock.viewOnly);

    if (!result.canComplete && result.reason) {
      app.showActionNotification("Action Disallowed", result.reason, "default");
    }

    return !result.canComplete;
  }

  function getDependenciesIcon() {
    if (!selected || app.authLevel < 4 || props.multiSelect || lock.viewOnly) {
      return <DependenciesIcon delayDuration={tooltipDelay} className={task.dependencies.length > 0 ? "mx-2" : "mx-2 text-gray-500"} />;
    }
    return <Dialog open={dialogOpen} onOpenChange={setDialogOpen} >
      <DialogTrigger disabled={task.completed} onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setDialogOpen(true);
      }}>
        <div >
          <DependenciesIcon delayDuration={tooltipDelay} className={task.dependencies.length > 0 ? "mx-2" : "mx-2 text-gray-500"} />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Dependencies</DialogTitle>
          <DialogDescription>
          </DialogDescription>
        </DialogHeader>
        <FrmDependencies
          task={task}
          onSubmit={(updateSelectedDependencies)}
        />
      </DialogContent>
    </Dialog>;
  }

  function updateSelectedDependencies(selectedDependencies: Array<number>) {
    task.dependencies = selectedDependencies;
    if (task.dependencies.length > 0) {
      const earliestStart = getEarliestStartDate(task, getDependencyOptions());
      setEarliestStartDate(earliestStart);
      task.startDate = earliestStart;
      task.finishDate = props.addWorkingDays(task.startDate, task.duration);
    } else {
      const workPackage = props.workstreams.find((ws) =>
        ws.workPackages.some((wp) => wp.tasks.some((t) => t.id === task.id)))?.workPackages
        .find((wp) => wp.tasks.some((t) => t.id === task.id));

      task.startDate = workPackage?.startDate ? new Date(workPackage.startDate) : new Date(props.projectStartDate);
      task.finishDate = props.addWorkingDays(task.startDate, task.duration);
    }
    props.updateTask(task.id, task);
    setDialogOpen(false);
  }

  function updateTaskComplete() {
    if (disableComplete()) {
      return;
    }

    task.completed = !task.completed;

    if (task.completed) {
      task.completedDate = new Date();
    } else {
      task.completedDate = null;
    }

    props.updateTask(task.id, task);
  }

  return (
    <div ref={ref} data-dragging={isDragging} style={style} className="select-none focus:outline-none">
      <div id={"t" + task.id} onMouseDown={(e) => {
        if (e.shiftKey) {
          e.preventDefault();
        }
      }}
        onClick={(event) => { props.multiSelect ? props.selectMultiTask(task.id, event) : props.selectTask(task.id, task.workPackageId, event) }} className={"flex flex-row select-none justify-between text-planbuilder-task-header-foreground items-center "
          + (primary ? "border-t-2 border-b-2 border-planbuilder-task-header-foreground " : "")
          + backgroundColor// + borderColor
          + (isDragging ? " opacity-50" : "")
          + " hover: cursor-pointer"}>
        <div className={"flex flex-col " + showGantt ? "min-w-[461px]" : "min-w-[501px]"} style={{ paddingLeft: subTaskDepth * 15 + "px" }}>
          <div className={"flex flex-row items-center pr-2"}>
            {getCompletedIcon()}{getTaskIcon()}
            <h2 className="flex pl-2">{getTaskNameField()}</h2>
          </div>
        </div>
        {!showGantt &&
          <>
            <div className="flex flex-row flex-shrink flex-grow w-0 overflow-hidden">
              <div className="flex flex-col px-2 min-w-20">{getTaskDurationField()}</div>
              <div className="flex flex-col w-[60%] pl-[2.5rem]">
                {getTaskOwnerField()}
              </div>
              {/* <div>{task.id}</div> */}
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex flex-row w-min items-center">
                {!showGantt &&
                  <>
                    <div className="flex flex-row justify-start">{getDisplayOnStatusReportIcon()}{getDisplayOnCharterIcon()}</div>
                    <div className="">{getStatusIcon()}</div>
                    {getDependenciesIcon()}
                  </>}
                <div className="flex flex-col w-[175px]">
                  {getDateRangeField()}
                </div>
                {app.authLevel >= 4 && !lock.viewOnly && subTaskDepth < 2 ? (
                  <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.addTask(task.workPackageId, task); }} className="p-0 bg-transparent border-0">
                    <AddIcon delayDuration={tooltipDelay} className="m-2" tooltip="Add Sub Task" />
                  </button>)
                  : <div className="m-2 w-[25px]" />}
              </div>
            </div>
          </>
        }
        {localSubTasks.length > 0 ?
          <button onClick={(e) => { e.stopPropagation(); setOpen(!open) }} className="px-2 py-2 h-9">{open ? <ChevronUpIcon delayDuration={tooltipDelay} /> : <ChevronRightIcon delayDuration={tooltipDelay} />}</button> : <div className="min-w-[40px]" />}
        {showGantt &&
          <GanttObj
            startDate={task.startDate}
            finishDate={task.finishDate}
            color={backgroundColor}
            completedDate={task.completedDate ?? undefined}
          />}
      </div>
      {open && localSubTasks.length > 0 && (
        <DragDropProvider
          onDragStart={({operation}) => {
            if (isSortable(operation.source)) {
              const taskId = parseInt(operation.source.id.toString().substring(1));
              const draggedTask = props.getTaskById(taskId);
              if (!draggedTask) {
                return;
              }
              draggedTask.isMoving = true;
              setMovingTask(draggedTask);
              props.closeAllSiblingsForDragging();
            }
          }
          }
          onDragEnd={({ operation }) => {
            const { source, target } = operation;
            
            if (target && isSortable(source) && isSortable(target)) {
              const sourceIndex = source.sortable.initialIndex;
              const targetIndex = target.sortable.index;

              if (sourceIndex !== undefined && targetIndex !== undefined && task.subTasks) {
                // Ensure we're working with sorted data (by saved index)
                const sortedSubTasks = [...task.subTasks].sort((a, b) => a.index - b.index);
                // Always use the fresh sorted array, not the potentially stale localSubTasks
                const reorderedSubtasks = arrayMove(sortedSubTasks, sourceIndex, targetIndex);
                const updatedSubTasks = reorderedSubtasks.map((st, idx) => ({ ...st, index: idx }));
                setLocalSubTasks(updatedSubTasks);
                task.subTasks = updatedSubTasks;
                props.saveSubTaskOrder(task.id, task.subTasks);
              }
            } else {
              // No valid target - restore correct order and force re-render
              if (task.subTasks) {
                const sorted = [...task.subTasks].sort((a, b) => a.index - b.index);
                setLocalSubTasks(sorted);
                setRenderVersion(prev => prev + 1);
                app.showActionNotification("Task Moved", "Task was not moved as it was not dropped in a valid location", "default");
              }
            }
            setMovingTask(null);
            props.openSiblingsAfterDragging();
          }}
          sensors={[
            PointerSensor.configure({
              activationConstraints: {
                distance: {
                  value: 25,
                  tolerance: { x: 200, y: 25 },
                },

                delay: {
                  value: 1000,
                  tolerance: 10,
                }
              },
            })
          ]}
        >
          <div className={"flex flex-col " + backgroundColor}>
            {localSubTasks.map((subTask, idx) => (
              <TaskObj
                key={`${subTask.id}-${renderVersion}`}
                task={subTask}
                selected={props.multiSelect ? props.selectedTaskIDs.includes(subTask.id) : props.selectedTaskID === subTask.id}
                primary={props.multiSelect ? props.selectedTaskID === subTask.id : false}
                showGantt={showGantt}
                index={idx}
                subTaskDepth={subTaskDepth + 1}
                groupId={"t" + task.id}
              />
            ))}
          </div>
          <DragOverlay>
            {movingTask ? (
              <TaskObj
                task={{ ...movingTask, isMoving: false }}
                selected={true}
                primary={true}
                showGantt={showGantt}
                index={localSubTasks.findIndex(st => st.id === movingTask.id)}
                subTaskDepth={subTaskDepth + 1}
                groupId={"t" + task.id}
                expanded={false}
              />
            ) : null}
          </DragOverlay>
        </DragDropProvider>
      )}
    </div >
  )
}

export default TaskObj;